import React, {
	lazy,
	Suspense,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";

import { Timeline } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import {
	AppBar,
	Box,
	Drawer,
	IconButton,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useHasPermission } from "../../context/userContext";
import { NavbarContext } from "../../globalContext";
import { ErrorModal } from "../modals/error-modal";
import { TimeAndUserMenu } from "./TimeAndUserMenu";

const AreaSelector = lazy(() =>
	import("./areaSelector/area-selector").then((mod) => ({
		default: mod.AreaSelector,
	})),
);

export default function Header({
	showAreaSelector = false,
	showTrendingsV2Icon = true,
	navbarTitle = "MESF",
}: {
	showAreaSelector?: boolean;
	showTrendingsV2Icon?: boolean;
	navbarTitle?: string;
}) {
	const [error, setError] = useState("");
	const CustomNavbar = useContext(NavbarContext);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const hasPermissionTo = useHasPermission();
	const theme = useTheme();
	const navContainerRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const isMobileBreakpoint = useMediaQuery(theme.breakpoints.down("lg"));

	const handleDrawerToggle = () => {
		setDrawerOpen(!drawerOpen);
	};

	const closeDrawer = () => {
		setDrawerOpen(false);
	};

	useEffect(() => {
		const container = navContainerRef.current;
		if (!container) return;

		const observer = new ResizeObserver(() => {
			setIsOverflowing(container.scrollWidth > container.offsetWidth);
		});

		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const handleMenuItemClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			// Check if the clicked element or its parents is a MenuItem
			const menuItem = target.closest(".MuiMenuItem-root");

			if (menuItem) {
				setDrawerOpen(false);
			}
		};

		// Add the event listener to the document
		document.addEventListener("click", handleMenuItemClick);

		// Cleanup
		return () => {
			document.removeEventListener("click", handleMenuItemClick);
		};
	}, []);

	const canShowSettings = hasPermissionTo("ui.navbar.settings");
	const canShowAreaSelector = hasPermissionTo("SEC.UpsertDefaultAssetId");
	const isCompactNavigation = isMobileBreakpoint || isOverflowing;

	return (
		<>
			<AppBar
				position="static"
				sx={{
					backgroundColor: (theme) => theme.palette.primary.main,
					zIndex: (theme) => theme.zIndex.modal + 1,
				}}
			>
				<Toolbar
					sx={{
						backgroundColor: (theme) => theme.palette.primary.main,
						minWidth: 0,
						overflowX: "hidden",
					}}
				>
					{isCompactNavigation && (
						<IconButton
							edge="start"
							sx={{
								flexShrink: 0,
								marginRight: (theme) => theme.spacing(2),
								textDecoration: "none",
								color: "white",
								textWrap: "nowrap",
							}}
							color="inherit"
							aria-label="menu"
							onClick={handleDrawerToggle}
						>
							<MenuIcon />
						</IconButton>
					)}

					<Typography
						variant="h6"
						component={Link}
						to="/home"
						sx={{
							flexShrink: 0,
							marginRight: (theme) => theme.spacing(2),
							textDecoration: "none",
							color: "white",
							textWrap: "nowrap",
						}}
					>
						{navbarTitle}
					</Typography>

					<Box
						ref={navContainerRef}
						sx={{
							display: "flex",
							flexGrow: 1,
							minWidth: 0,
							overflow: "hidden",
							visibility: isCompactNavigation ? "hidden" : "visible",
							pointerEvents: isCompactNavigation ? "none" : "auto",
							"& a": {
								color: "white",
							},
							"& > *": {
								flexShrink: 0,
							},
						}}
						aria-hidden={isCompactNavigation}
					>
						<CustomNavbar closeDrawer={closeDrawer} />
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							color: "white",
							flexShrink: 0,
						}}
					>
						{showTrendingsV2Icon && (
							<Tooltip
								title="Trendings V2 (ECharts)"
								placement="bottom"
								arrow
								enterDelay={1500}
							>
								<IconButton color="inherit" component={Link} to="/trendings-v2">
									<Timeline />
								</IconButton>
							</Tooltip>
						)}

						{canShowSettings && (
							<Tooltip title="Settings" placement="bottom">
								<IconButton
									color="inherit"
									component={Link}
									to="/configuration"
								>
									<SettingsIcon />
								</IconButton>
							</Tooltip>
						)}

						{showAreaSelector && canShowAreaSelector && (
							<Suspense fallback={<div>...</div>}>
								<AreaSelector />
							</Suspense>
						)}

						<TimeAndUserMenu />
					</Box>
				</Toolbar>
			</AppBar>

			<Drawer
				variant="temporary"
				anchor="left"
				open={drawerOpen}
				onClose={handleDrawerToggle}
				sx={{
					width: 250,
					"& .MuiDrawer-paper": {
						width: 250,
						top: { xs: 56, sm: 64 },
						height: {
							xs: "calc(100% - 56px)",
							sm: "calc(100% - 64px)",
						},
						backgroundColor: (theme) => theme.palette.grey[100], // Light grey background
						// Alternatively, you can use a specific color:
						// backgroundColor: '#f5f5f5',
					},
				}}
				ModalProps={{
					keepMounted: true,
				}}
			>
				<Box
					sx={{
						width: "100%",
						backgroundColor: (theme) => theme.palette.grey[100], // Light grey background to match
						// backgroundColor: '#f5f5f5', // If using a specific color above
						"& a, & button": {
							width: "100%",
							justifyContent: "flex-start",
							padding: (theme) => theme.spacing(1, 2),
							color: (theme) => theme.palette.text.primary,
						},
					}}
				>
					<CustomNavbar isMobile={true} closeDrawer={closeDrawer} />
				</Box>
			</Drawer>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
}
